/**
 * Builds tooltip text for verification status.
 * Use only where tooltip is rendered; keep all logic here.
 */
import type { TooltipError } from './tooltip-error.enum';

export type VerificationStatusForTooltip = 'VERIFIED' | 'UNVERIFIED' | 'ERROR' | 'SKIPPED';

/**
 * Visual severity for three-state verification display.
 * - green: verified / all clear
 * - yellow: inconclusive — not necessarily a problem but needs attention
 * - red: failed / blocked / action required
 */
export type VerificationSeverity = 'green' | 'yellow' | 'red';

export interface VerificationTooltipData {
  severity: VerificationSeverity;
  label: string;
  badge: string | null;
  title: string;
  body: string;
  guidance: string | null;
  lastVerified: string | null;
}

/**
 * Map a provider status + error list to one of three visual states.
 * Severity rules (applied in order):
 * 1. VERIFIED with no errors → green
 * 2. SKIPPED → yellow (check was not run)
 * 3. UNVERIFIED with `isInconclusive` flag → yellow (account exists but couldn't confirm)
 * 4. Everything else (ERROR, UNVERIFIED with real failures) → red
 */
export function resolveVerificationSeverity(
  status: VerificationStatusForTooltip,
  isInconclusive = false
): VerificationSeverity {
  if (status === 'VERIFIED') return 'green';
  if (status === 'SKIPPED') return 'yellow';
  if (status === 'UNVERIFIED' && isInconclusive) return 'yellow';
  return 'red';
}

const SEVERITY_LABELS: Record<VerificationSeverity, string> = {
  green: 'Verified',
  yellow: 'Unverified',
  red: 'Unverified',
};

const SEVERITY_BADGES: Record<VerificationSeverity, string | null> = {
  green: null,
  yellow: 'Inconclusive',
  red: null,
};

interface BuildTooltipOptions {
  status: VerificationStatusForTooltip;
  lastVerified?: string | null | undefined;
  errors?: TooltipError[];
  isInconclusive?: boolean;
  guidance?: string | null | undefined;
}

export function buildVerificationTooltipData(opts: BuildTooltipOptions): VerificationTooltipData {
  const {
    status,
    lastVerified = null,
    errors = [],
    isInconclusive = false,
    guidance: guidanceOverride,
  } = opts;

  const severity = resolveVerificationSeverity(status, isInconclusive);

  const label = SEVERITY_LABELS[severity];
  const badge = SEVERITY_BADGES[severity];

  let title: string;
  let body: string;
  let guidance: string | null;

  switch (severity) {
    case 'green':
      title = 'Verified';
      body = 'This check passed successfully.';
      guidance = null;
      break;
    case 'yellow':
      if (status === 'SKIPPED') {
        title = 'Not checked';
        body = 'This verification was not run.';
        guidance =
          guidanceOverride ?? 'Verification may run automatically or can be triggered manually.';
      } else {
        title = 'Unverified \u2014 Inconclusive';
        body =
          errors.length > 0
            ? errors.join('\n')
            : 'The check ran but could not produce a definitive result.';
        guidance =
          guidanceOverride ??
          'This does not necessarily indicate fraud \u2014 the account may be new or the bank may not participate in automated verification.';
      }
      break;
    default:
      title = 'Unverified';
      if (status === 'ERROR' && errors.length === 0) {
        body = 'We couldn\u2019t complete the verification check.';
        guidance =
          guidanceOverride ?? 'Please try again later. If the problem persists, contact support.';
      } else {
        body = errors.length > 0 ? errors.join('\n') : 'Verification failed.';
        guidance =
          guidanceOverride ??
          'Review the account details and correct any issues, then retry verification.';
      }
  }

  return { severity, label, badge, title, body, guidance, lastVerified };
}

/**
 * Returns a single string suitable for tooltip display (use whitespace-pre-line when rendering).
 * Kept for backward compatibility — new callers should prefer buildVerificationTooltipData.
 */
export function buildVerificationTooltip(
  status: VerificationStatusForTooltip,
  lastVerified?: string | null,
  errors: TooltipError[] = []
): string {
  if (status === 'VERIFIED') {
    return lastVerified ? `Last Verified: ${lastVerified}` : 'Verified';
  }

  if (status === 'UNVERIFIED' || (status === 'ERROR' && errors.length > 0)) {
    const lines: string[] = errors.length > 0 ? [...errors] : [];
    if (lastVerified) lines.push(`Last Verified: ${lastVerified}`);
    return lines.join('\n') || (status === 'ERROR' ? 'Verification check failed.' : 'Not verified');
  }

  if (status === 'ERROR') {
    const lines = ['We couldn\u2019t complete the verification check. Please try again later.'];
    if (lastVerified) lines.push(`Last Verified: ${lastVerified}`);
    return lines.join('\n');
  }

  if (status === 'SKIPPED') {
    const lines = ['The check was not run'];
    if (lastVerified) lines.push(`Last Verified: ${lastVerified}`);
    return lines.join('\n');
  }

  return '';
}
